/// <reference path="./global.d.ts" />

// import './wdyr'; -- Uncomment when testing component rerenders
import { GenericObj } from '@solvhealth/types/interfaces/generics';
// @ts-ignore ts-migrate(7016) FIXME: Try `npm install @types/query-string` if it exists... Remove this comment to see the full error message
import queryString from 'query-string';
import Raven from 'raven-js';
import React from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { persistStore } from 'redux-persist';
import i18n, { loadClientI18next } from '~/locale/i18nClient';
import App from './components/App';
import Main from './components/Main/Main';
import { ENV, NOTIFICATIONS_APPLICATION_SERVER_KEY, SENTRY_DSN } from './config/index';
import { analyticsTrackPage } from './core/analytics';
import { updateMeta } from './core/DOMUtils';
import history from './core/history';
import logger from './core/logger';
import registerServiceWorker from './core/util/registerServiceWorker';
import { deleteUrlParamsCookie } from './core/util/tracking';
import ErrorPage from './routes/error/ErrorPage';
import configureStore, { getClientInitialState } from './store/configureStore';

window.addEventListener('unhandledrejection', (event) => {
  // Prevent error output on the console
  event.preventDefault();
});

const serviceWorkerPath = '/serviceWorker.js';

const { swRegistration, onLocationChangeWrapper } = registerServiceWorker(serviceWorkerPath);

const notificationInfo = {
  applicationServerKey: NOTIFICATIONS_APPLICATION_SERVER_KEY,
  swRegistration,
};

if (SENTRY_DSN !== null) {
  Raven.config(SENTRY_DSN, {
    environment: ENV,
    ignoreErrors: [
      'TypeError: Failed to fetch',
      'TypeError: cancelled',
      'TypeError: The Internet connection appears to be offline.',
      'Invariant Violation: unmountComponentAtNode(...): Target container is not a DOM element.',
      'TypeError: The operation couldn’t be completed. Software caused connection abort',
      'TypeError: The network connection was lost.',
      'TypeError: The request timed out.',
      "TypeError: NetworkError: The operation couldn't be completed. Software caused connection abort",
      "TypeError: undefined is not an object (evaluating 'e.originalEvent.origin')",
    ],
    release: __webpack_hash__, // jscs:disable requireCamelCaseOrUpperCaseIdentifiers
  }).install();
}

// @ts-expect-error ts-migrate(2339) FIXME: Property 'fbAsyncInit' does not exist on type 'Win... Remove this comment to see the full error message
window.fbAsyncInit = () => {
  // @ts-ignore
  window.FB?.init({
    appId: '903407596701015',
    autoLogAppEvents: true,
    xfbml: true,
    version: 'v4.0',
  });
};

const shutdownIntercom = () => {
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'Intercom' does not exist on type 'Window... Remove this comment to see the full error message
  if (typeof window.Intercom === 'function') {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'Intercom' does not exist on type 'Window... Remove this comment to see the full error message
    window.Intercom('shutdown');
  }
};

window.onbeforeunload = shutdownIntercom;

/* eslint-disable global-require */

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const { store } = configureStore(getClientInitialState());
const context = {
  store,
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  // @ts-expect-error ts-migrate(7019) FIXME: Rest parameter 'styles' implicitly has an 'any[]' ... Remove this comment to see the full error message
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map((x) => x._insertCss());
    return () => {
      removeCss.forEach((f) => f());
    };
  },

  setTitle: (value: string) => {
    document.title = value;
  },

  setMeta: updateMeta,
};

const container = document.getElementById('app');
let currentLocation = history.location;
let router = require('./router').default;

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory: GenericObj = {};
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const removeCssFromDocument = () => {
  const elm = document.getElementById('css');

  if (elm) {
    elm.parentNode?.removeChild(elm);
  }
};

interface OnRenderComplete {
  (route: any, location: typeof history.location): void;
}

const subsequentOnRenderComplete: OnRenderComplete = (route, location) => {
  analyticsTrackPage();

  if (route.title) {
    document.title = route.title;
  }

  if (route.description) {
    updateMeta('description', route.description);
  }

  let scrollX = 0;
  let scrollY = 0;

  const pos = location.key && scrollPositionsHistory[location.key];
  if (pos) {
    scrollX = pos.scrollX;
    scrollY = pos.scrollY;
  } else {
    const targetHash = location.hash.substr(1);
    if (targetHash) {
      const target = document.getElementById(targetHash);
      if (target) {
        scrollY = window.pageYOffset + target.getBoundingClientRect().top;
      }
    }
  }

  // Restore the scroll position if it was saved into the state
  // or scroll to the given #hash anchor
  // or scroll to top of the page
  window.scrollTo(scrollX, scrollY);
};
const initialOnRenderComplete = () => {
  removeCssFromDocument();

  // PERSIST STORE ONLY ONCE THE CLIENT SIDE RENDERING ALREADY HAPPENED
  // if you really want to change it, I hope you know what you are doing
  persistStore(context.store);

  analyticsTrackPage();
  deleteUrlParamsCookie();
};

let onRenderComplete: OnRenderComplete = () => {
  initialOnRenderComplete();

  onRenderComplete = subsequentOnRenderComplete;
};

/**
 * Re-render the app when window.location changes.
 *
 * @param location new route
 * @param action route change way
 */
async function onLocationChange(location: typeof history.location, action?: any) {
  // Remember the latest scroll position for the previous location
  if (currentLocation.key) {
    scrollPositionsHistory[currentLocation.key] = {
      scrollX: window.pageXOffset,
      scrollY: window.pageYOffset,
    };
  }

  // Delete stored scroll position for next page if any
  if (action === 'PUSH' && location.key) {
    delete scrollPositionsHistory[location.key];
  }

  try {
    await loadClientI18next();
  } catch (e) {
    logger.error('Could not load i18next');
    logger.error(e);
  }

  currentLocation = location;
  const isInitialRender = !action;
  const renderApp = isInitialRender ? ReactDOM.hydrate : ReactDOM.render;
  try {
    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve({
      ...context,
      pathname: location.pathname,
      query: queryString.parse(location.search),
    });

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    renderApp(
      <I18nextProvider i18n={i18n}>
        {/* @ts-ignore */}
        <Provider store={store}>
          <Main context={context} notificationInfo={notificationInfo} route={route} />
        </Provider>
      </I18nextProvider>,
      container,
      () => onRenderComplete(route, location)
    );
  } catch (error) {
    // Do a full page reload if error occurs during client-side navigation
    if (action && currentLocation.key === location.key) {
      window.location.reload();
    }

    ReactDOM.hydrate(
      <I18nextProvider i18n={i18n}>
        {/* @ts-ignore */}
        <Provider store={store}>
          <App context={context}>
            <ErrorPage error={error} />
          </App>
        </Provider>
      </I18nextProvider>,
      container
    );
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChangeWrapper(onLocationChange));

onLocationChange(currentLocation);

// Disable Hot Module Replacement (HMR)
/*
if (module.hot) {
  module.hot.accept('./routes', () => {
    router = require('./routes').default;

    if (appInstance) {
      try {
        // Force-update the whole tree, including components that refuse to update
        deepForceUpdate(appInstance);
      } catch (error) {
        appInstance = null;
        document.title = `Hot Update Error: ${error.message}`;
        console.error(error);
        return;
      }
    }

    onLocationChange(currentLocation);
  });
}
*/
